import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { useNavigate } from "react-router-dom";
import IconComponent from "../../cubeComponent/icons";
import {
  getProjectContractPageUrl,
  getProjectDashboardPageUrl,
  getProjectEstimatesPageUrl,
  getProjectFilesPageUrl,
  getProjectAssetsPageUrl,
  getProjectGeneralClashPageUrl,
  getProjectIssuesPageUrl,
  getProjectPhotosPageUrl,
  getProjectMailboxPageUrl,
  getProjectReviewsPageUrl,
  getProjectSchedulesPageUrl,
  getProjectTasksPageUrl,
  getProjectTransmittalsPageUrl,
  getProjectSettingsPageUrl,
  getProjectWorkspacesListPageUrl,
  getProjectFoldersPageUrl,
  getProjectFormsPageUrl,
} from "@Features/projects/utils/projectUrls";
import useStickySidebar, { type StickySidebarIcon } from "./useStickySidebar";
import ProjectLayouts from "@Features/projects/components/ProjectLayouts";
import {
  PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY,
  LAYOUTS_SIDEBAR_CELL_ID,
  SIDEBAR_LAYOUTS_TOGGLE_ATTR,
  getLayoutViewKey,
} from "@Helpers/constants";
import type { LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import { getLayoutModuleIds } from "@Features/projects/utils/layoutModules";
import type { SidebarIcon } from "@Features/projects/components/ProjectLayouts/ConfigureLayoutModal";
import { getProjectObject } from "@Actions/projectSetting";
import { setSelectedProjectLayout } from "@Reducers/stickySidebar";
import axiosWrapper from "@Services/axiosWrapper";
import { untranslated, useTranslation } from "@i18n";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { Box, ButtonBase, Chip, Divider, Drawer, List, ListItem, ListItemIcon, useMediaQuery } from "@mui/material";
import { decideScrollBars, sameScrollBars, type ScrollBars } from "./scrollBars";
import * as Sentry from "@Sentry";

interface SidebarProps {
  userData?: { organisation?: { name?: string } };
  /** The route's `?projectId`: every entry links into this project, so Router mounts the sidebar only with one. */
  projectId: string;
}

/** Pins the bar's height before the icon's async SVG resolves, so it doesn't pop from 1.5rem to 2.5rem. */
const CHEVRON_BOX = { height: "1rem", width: "1rem" } as const;

/** Tailwind's `max-sm` (below 640px). Media-query em is the 16px initial font, not the 12px phone root. */
const PHONE_QUERY = "(max-width:39.99875em)";
/** Tailwind's `max-md` (below 768px): every phone run, the 750px landscape one included. */
const BELOW_MD_QUERY = "(max-width:47.99875em)";
const BELOW_MD_MEDIA = `@media ${BELOW_MD_QUERY}`;

/** Set (to "no") while the user keeps the drawer collapsed; the classic shell's only persisted nav state. */
const SIDEBAR_COLLAPSED_KEY = "isSideBarExpanded";

/**
 * Phones always open collapsed: the expanded drawer (15.625rem) would leave a 360px phone about 170px of
 * page. Elsewhere the stored preference decides.
 */
const expandedByDefault = (isPhone: boolean) => !isPhone && !localStorage.getItem(SIDEBAR_COLLAPSED_KEY);

/**
 * md and up the module list scrolls only through the bars, as it always has. Below md it also scrolls by
 * touch, with its scrollbar hidden: a classic scrollbar would take width from the labels and re-wrap them.
 */
const MID_LIST_SX = {
  overflow: "hidden",
  flex: 1,
  minHeight: 0,
  [BELOW_MD_MEDIA]: {
    overflowX: "hidden",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
} as const;

/** The classic up bar lies over the list's top edge. Logical insets because the app runs RTL. */
const OVERLAY_BAR_SX = { position: "absolute", top: 0, insetInlineStart: 0, insetInlineEnd: 0, zIndex: 1 } as const;
const NO_SCROLL_BARS: ScrollBars = { up: null, down: null };

/** The project sidebar's route builders: pure functions of the project id, so one module-level object serves
 *  every render (`useStickySidebar` lists it as a dependency). */
const SIDEBAR_PATHS = {
  Contract: (projectId: string) => getProjectContractPageUrl(projectId),
  Dashboard: (projectId: string) => getProjectDashboardPageUrl(projectId),
  Estimates: (projectId: string) => getProjectEstimatesPageUrl(projectId),
  Files: (projectId: string) => getProjectFilesPageUrl(projectId),
  Assets: (projectId: string) => getProjectAssetsPageUrl(projectId),
  GeneralClash: (projectId: string) => getProjectGeneralClashPageUrl(projectId),
  Issues: (projectId: string) => getProjectIssuesPageUrl(projectId),
  Photos: (projectId: string) => getProjectPhotosPageUrl(projectId),
  Mailbox: (projectId: string) => getProjectMailboxPageUrl(projectId),
  Reviews: (projectId: string) => getProjectReviewsPageUrl(projectId),
  Schedules: (projectId: string) => getProjectSchedulesPageUrl(projectId),
  Tasks: (projectId: string) => getProjectTasksPageUrl(projectId),
  Transmittals: (projectId: string) => getProjectTransmittalsPageUrl(projectId),
  Settings: (projectId: string) => getProjectSettingsPageUrl({ projectId, tab: "general_information" }),
  WorkspacesList: (projectId: string) => getProjectWorkspacesListPageUrl(projectId),
  // The Spaces sidebar icon now opens the project-level "Folders" tree directly.
  Folders: (projectId: string) => getProjectFoldersPageUrl(projectId),
  Forms: (projectId: string, moduleName: string) => getProjectFormsPageUrl(projectId, moduleName),
};

function Sidebar({ userData, projectId }: SidebarProps) {
  const layout = useSelector((state: RootState) => state.layout);
  const activeCell = useSelector((state: RootState) => state.currentSelectedSidebarCell);
  const selectedProjectLayout = useSelector((state: RootState) => state.selectedProjectLayout);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // noSsr: the first render already knows the viewport, so a phone never paints the expanded drawer.
  const isPhone = useMediaQuery(PHONE_QUERY, { noSsr: true });
  // Which up/down bar layout the module list gets (scrollBars.ts): the in-flow pair on phones, the classic bars
  // from md up. The same breakpoint as the paper's BELOW_MD_MEDIA.
  const scrollBarMode = useMediaQuery(BELOW_MD_QUERY, { noSsr: true }) ? "pair" : "classic";
  // The user's last toggle, tagged with the breakpoint it was made at. Derived rather than synced by an
  // effect, so the first render is already right and the drawer and its column can never disagree;
  // crossing the phone breakpoint (rotation, a resized window) falls back to that size's default.
  const [toggled, setToggled] = useState<{ isPhone: boolean; expanded: boolean } | null>(null);
  const isExpanded = toggled !== null && toggled.isPhone === isPhone ? toggled.expanded : expandedByDefault(isPhone);
  const [showLayoutsModal, setShowLayoutsModal] = useState(false);
  const selectedLayoutId: string | undefined = selectedProjectLayout
    ? getLayoutViewKey(selectedProjectLayout)
    : undefined;
  // Which projectId the applied selectedProjectLayout belongs to. selectedProjectLayout is global
  // (not keyed by project), so this lets us tell "layout for the current project is loaded" apart
  // from "stale / still loading" — without flashing a fallback list in between.
  const [layoutLoadedForProjectId, setLayoutLoadedForProjectId] = useState<string | null>(null);
  // Module ids come straight from the stored layout doc; custom module names are
  // embedded server-side at write time.
  const selectedLayoutModules = useMemo(() => getLayoutModuleIds(selectedProjectLayout), [selectedProjectLayout]);
  // A pure function of content height, column height and scroll position (decideScrollBars).
  const [scrollBars, setScrollBars] = useState<ScrollBars>(NO_SCROLL_BARS);

  const midIconsDivRef = useRef<HTMLUListElement | null>(null);
  const midColumnRef = useRef<HTMLDivElement | null>(null);

  const { sideBarMidIcons, sideBarUpperFixedIcons, sideBarLowerFixedIcons } = useStickySidebar({
    layout,
    projectId,
    path: SIDEBAR_PATHS,
  });

  const visibleMidIcons = useMemo(() => {
    // Until the selected layout for THIS project has resolved, render nothing — NOT the "All Modules"
    // fallback. Showing every module during the fetch window and then narrowing to the saved subset
    // is the visible flicker (blank → all modules → subset); painting once, when the layout is ready,
    // removes it. Staleness is tracked by projectId, so a project switch (or a re-run of the fetch
    // effect) never briefly shows the previous project's list or the full module set.
    if (layoutLoadedForProjectId !== projectId) return [];
    // Resolved, but no usable selection (deleted selection or a transient fetch failure) → fall back
    // to every enabled module so the sidebar is never permanently empty.
    if (!selectedProjectLayout) return sideBarMidIcons;
    return Array.isArray(selectedLayoutModules)
      ? selectedLayoutModules
          .map((moduleId: string) => sideBarMidIcons.find((icon: { id: string }) => icon.id === moduleId))
          // A saved module id that no longer resolves to an enabled icon is dropped; the predicate is
          // spelled out so the holes leave the TYPE too, not just the array.
          .filter((icon): icon is StickySidebarIcon => icon !== undefined)
      : [];
  }, [layoutLoadedForProjectId, projectId, selectedProjectLayout, selectedLayoutModules, sideBarMidIcons]);

  useEffect(() => {
    const el = midIconsDivRef.current;
    const column = midColumnRef.current;
    // Both are rendered unconditionally by the permanent drawer, so they are attached by the time an
    // effect runs; React only types refs as nullable.
    if (!el || !column) return;

    const update = () => {
      const next = decideScrollBars(
        {
          contentHeight: el.scrollHeight,
          wrapperHeight: column.clientHeight,
          scrollTop: el.scrollTop,
          clientHeight: el.clientHeight,
        },
        scrollBarMode,
      );
      // Keep the previous object when nothing changed, so a scroll event does not re-render the sidebar.
      setScrollBars((prev) => (sameScrollBars(prev, next) ? prev : next));
    };

    const resizeObserver = new ResizeObserver(update);
    // 1. The column (window/drawer resizes) and the list's own box (the bars mounting).
    resizeObserver.observe(column);
    resizeObserver.observe(el);
    // 2. Every row. Load-bearing: the list is `flex: 1` in a fixed-height column, so its own box
    //    never changes when its content does and an observer on it alone cannot see rows growing
    //    after the rAF below — icon SVGs resolve async, labels re-wrap during the width transition.
    Array.from(el.children).forEach((row) => resizeObserver.observe(row));

    // 3. Observe scroll changes
    el.addEventListener("scroll", update, { passive: true });

    // 4. Initial check AFTER mount
    requestAnimationFrame(update);

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
    // `visibleMidIcons`, not its length: rows are keyed by module id, so a same-length layout switch
    // replaces every <li> and would leave the observer on detached nodes.
  }, [midIconsDivRef, isExpanded, visibleMidIcons, scrollBarMode]);

  const handleUpArrowClick = () => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      scrollableContent.scrollBy({ top: -100, behavior: "smooth" });
    }
  };

  const handleDownArrowClick = (customHeight = 100) => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      scrollableContent.scrollBy({ top: customHeight, behavior: "smooth" });
    }
  };

  const applySelectedLayout = useCallback(
    (layoutDoc: LayoutData | null) => {
      dispatch(setSelectedProjectLayout(layoutDoc ?? null));
    },
    [dispatch],
  );

  // Stable identity so ProjectLayouts' outside-click listener attaches once per open/close of the
  // flyout rather than re-subscribing on every Sidebar render.
  const closeLayoutsModal = useCallback(() => setShowLayoutsModal(false), []);

  // `SidebarIcon` declares `allow?: boolean`, while the sidebar's own `allow` carries a route builder
  // or a layout-module record (see useStickySidebar). ProjectLayouts reads only id/name/svg, so it is
  // handed exactly those instead of being told the two shapes are the same. Memoised because
  // ConfigureLayoutModal keys its memos off this array's identity.
  const layoutModuleIcons: SidebarIcon[] = useMemo(
    () => sideBarMidIcons.map(({ id, name, svg }) => ({ id, name, svg })),
    [sideBarMidIcons],
  );

  useEffect(() => {
    if (!projectId) return;
    dispatch(getProjectObject({ id: projectId }));
    // The fetch can land after the sidebar has moved on to another project, and the guards that drop
    // that late result have to survive type narrowing: a plain `let active = true` is only ever
    // written from the cleanup closure, so TS pins it to the literal `true` and reads every guard
    // below as statically dead. `signal.aborted` is a real `boolean` the checker cannot collapse.
    const abortOnProjectChange = new AbortController();
    void (async () => {
      try {
        const response = await axiosWrapper.api.post("/layoutView/getSelectedViewerLayout", {
          key: PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY,
          projectId,
        });
        if (abortOnProjectChange.signal.aborted) return;
        // An answered failure is the same "no saved selection" case as a success without `data`:
        // visibleMidIcons reads a null selection as "show every module".
        applySelectedLayout(response.data.status ? (response.data.result ?? null) : null);
      } catch {
        if (abortOnProjectChange.signal.aborted) return;
        // On a transient failure fall back to the "All Modules" default (visibleMidIcons treats a null
        // selection as "show everything"), and let the user know it couldn't load.
        applySelectedLayout(null);
        dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
      } finally {
        // Mark the selection resolved for THIS project (success or handled failure) — visibleMidIcons
        // stays blank until this matches the current projectId, so no fallback flashes mid-fetch. We
        // intentionally do NOT reset the layout to null up-front: keeping the last one applied means a
        // re-run of this effect (or an unrelated re-render) never re-flashes the sidebar.
        if (!abortOnProjectChange.signal.aborted) setLayoutLoadedForProjectId(projectId);
      }
    })();
    return () => {
      abortOnProjectChange.abort();
    };
  }, [projectId, applySelectedLayout, dispatch, t]);

  // `ENABLE` is either a boolean or the conditional form `{ CONDITION: { COMPANY_NAME } }` that
  // limits Settings to one organisation (see portal.schema.ts); any other value leaves Settings
  // enabled. Deriving this during render (rather than latching it) is only safe because
  // `PROJECT_SETTINGS` is never partially rewritten: `GET /portal/getLayoutData` returns the portal's
  // whole stored `metaData`, the only runtime writes are the dotted `metaData.<MODULE>.ENABLE` paths
  // in modulesLayoutMapper (which has no PROJECT_SETTINGS entry) and `metaData.ALLOWED_FILE_TYPES`,
  // and `setLayout` is dispatched only by `fetchLayout`. A mid-session refetch therefore cannot drop
  // a stored `ENABLE: false` and silently re-enable Settings.
  const settingsEnable = layout.PROJECT_SETTINGS?.ENABLE;
  // `typeof null === "object"`, so the truthiness test is load-bearing: a portal storing
  // `ENABLE: null` would otherwise dereference `.CONDITION` and throw while rendering the sidebar.
  const settingsCompanyName =
    settingsEnable && typeof settingsEnable === "object" ? settingsEnable.CONDITION?.COMPANY_NAME : undefined;
  const showSettings =
    settingsEnable === false
      ? false
      : settingsCompanyName
        ? userData?.organisation?.name === settingsCompanyName
        : true;

  const handleSideBarExpandClick = () => {
    if (isExpanded) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, "no");
    } else {
      localStorage.removeItem(SIDEBAR_COLLAPSED_KEY);
    }
    setToggled({ isPhone, expanded: !isExpanded });
  };
  const toggleLabel = isExpanded ? t("Collapse sidebar") : t("Expand sidebar");

  const handleRowClick = (id: string, path?: string) => {
    if (id === LAYOUTS_SIDEBAR_CELL_ID) {
      setShowLayoutsModal((prev) => !prev);
      return;
    }
    // Layouts, handled above, is the only entry without a route: useStickySidebar builds every other one
    // from a builder this component supplies. A missing route is a bug that would swallow the click.
    if (path === undefined) {
      Sentry.captureException(new Error(`StickySidebar: sidebar entry ${id} has no route`));
      dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
      return;
    }
    // Fire-and-forget: router navigation is the last thing this handler does.
    void navigate(path);
  };

  // Layouts opens a flyout instead of navigating, so no page dispatches setSidebarSelectedCell for
  // it. Highlight it locally while open; the real page cell takes the highlight back on close.
  const highlightedCell = showLayoutsModal ? LAYOUTS_SIDEBAR_CELL_ID : activeCell;

  const getBetaTag = () => (
    <Chip
      size="small"
      sx={{
        fontWeight: 400,
        background: "#666a84",
        color: "white",
      }}
      label="BETA"
    />
  );
  const getIconsList = ({
    iconsList,
    type,
    ref = undefined,
    css,
  }: {
    iconsList: StickySidebarIcon[];
    type?: string;
    ref?: React.Ref<HTMLUListElement>;
    /** Merged into the List's `sx` literal, so a plain record rather than the SxProps union. */
    css?: Record<string, unknown>;
    /** Accepted by one call site but unused. */
    height?: string;
  }) => {
    return (
      <List
        ref={ref}
        sx={{
          paddingBottom: "0.625rem",
          border: "none",
          display: "flex",
          paddingTop: 0,
          flexDirection: "column",
          gap: "0",
          ...css,
        }}
      >
        {iconsList.map(({ toolTip, path, name, svg, id, showBetaTag }) => (
          <ListItem
            key={id}
            disablePadding
            data-testid={name + "_SidebarIcons"}
            // Gate only the Settings icon when Project Settings is disabled — never the whole
            // bottom group, so sibling icons like Layouts stay clickable.
            sx={{
              opacity: type == "settings" && id === "Settings" && !showSettings ? 0.5 : 1,
              pointerEvents: type == "settings" && id === "Settings" && !showSettings ? "none" : undefined,
            }}
            {...(id === LAYOUTS_SIDEBAR_CELL_ID ? { [SIDEBAR_LAYOUTS_TOGGLE_ATTR]: "true" } : {})}
          >
            <div className="flex justify-center py-4 items-center ps-2 max-md:py-2">
              {highlightedCell === id ? (
                <div
                  style={{ borderStartEndRadius: "0.375rem", borderEndEndRadius: "0.375rem" }}
                  className={`w-2 h-3 bg-[linear-gradient(#6bbbe3,#3e8ab0)] absolute inset-s-0`}
                ></div>
              ) : null}
              <ListItemIcon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <IconComponent
                  position="right"
                  onClick={() => handleRowClick(id, path)}
                  toolTipContent={toolTip === undefined ? undefined : untranslated(toolTip)}
                  showGradient={highlightedCell === id}
                  name={svg}
                  menuShow={isExpanded}
                  // Below md the icon (1.5rem = 18px on a phone) gets a 24px hit box: the root font is 12px
                  // below 1200px (index.css), so 2rem (`8`) is 24px there.
                  parentContainerClass={`flex gap-5 ${isExpanded ? "ps-4" : ""} max-md:min-w-8 max-md:min-h-8 max-md:items-center max-md:justify-center`}
                  width="1.5rem"
                  height="1.5rem"
                >
                  <div className="flex gap-3 items-center">
                    <span
                      className={`text-sm text-(--primary-text) ${highlightedCell === id ? "text-primary_gradient_color1" : ""}`}
                    >
                      {name}
                    </span>
                    {showBetaTag && getBetaTag()}
                  </div>
                </IconComponent>
              </ListItemIcon>
            </div>
          </ListItem>
        ))}
      </List>
    );
  };

  function getDrawer() {
    return (
      <Drawer
        sx={{
          width: isExpanded ? "15.625rem" : "4.5rem",
          "& .MuiDrawer-paper": {
            width: isExpanded ? "15.625rem" : "4.5rem",
            zIndex: 9,
            position: "fixed",
            paddingTop: "6.25rem",
            height: "100%",
            border: "none",
            display: "flex",
            overflow: "auto",
            backgroundColor: "#f0f5f9",
            // Below md the paper fills the sidebar column (the wrapper is `relative`) instead of being
            // fixed under a header of an assumed height: the phone header is taller (two rows), and the
            // 750px landscape run needs the 6.25rem pad back for the module list.
            [BELOW_MD_MEDIA]: { position: "absolute", top: 0, height: "100%", paddingTop: "0.5rem" },
          },
        }}
        variant="permanent"
        open={isExpanded}
        anchor={"left"}
      >
        <div>
          {getIconsList({ iconsList: sideBarUpperFixedIcons, height: "28.125rem", css: {} })}
          <Divider />
        </div>
        {/* The up/down bars and the list (scrollBars.ts). `relative` anchors the classic overlay up bar; below md
            `overflow-hidden`, since only the list scrolls there. */}
        <div ref={midColumnRef} className="relative flex overflow-auto flex-col h-full max-md:overflow-hidden">
          {scrollBars.up && (
            <Box
              onClick={handleUpArrowClick}
              id="upArrow"
              aria-disabled={!scrollBars.up.enabled}
              sx={scrollBars.up.overlay ? OVERLAY_BAR_SX : undefined}
              className={`flex flex-none bg-surface_neutral p-3 justify-center items-center max-md:p-0 max-md:min-h-8 ${
                scrollBars.up.enabled ? "cursor-pointer" : "pointer-events-none opacity-40"
              }`}
            >
              <span className="flex" style={CHEVRON_BOX}>
                <IconComponent name="BoldUpArrow" width="1rem" height="1rem" />
              </span>
            </Box>
          )}
          {getIconsList({
            iconsList: visibleMidIcons,
            type: "midIcons",
            ref: midIconsDivRef,
            css: MID_LIST_SX,
          })}
          {scrollBars.down && (
            <Box
              onClick={() => handleDownArrowClick()}
              aria-disabled={!scrollBars.down.enabled}
              className={`flex flex-none bg-surface_neutral justify-center items-center p-3 max-md:p-0 max-md:min-h-8 ${
                scrollBars.down.enabled ? "cursor-pointer" : "pointer-events-none opacity-40"
              }`}
            >
              <span className="flex" style={CHEVRON_BOX}>
                <IconComponent name="BoldDownArrow" width="1rem" height="1rem" />
              </span>
            </Box>
          )}
        </div>
        <div className="relative">
          <Divider />
          {getIconsList({ iconsList: sideBarLowerFixedIcons, type: "settings", css: { paddingTop: "0.875rem" } })}
          {showLayoutsModal && (
            <ProjectLayouts
              onClose={closeLayoutsModal}
              midIcons={layoutModuleIcons}
              projectId={projectId}
              isExpanded={isExpanded}
              selectedLayoutId={selectedLayoutId}
              onLayoutSelected={applySelectedLayout}
            />
          )}
        </div>
        {/* Below md the toggle is a row of its own: the edge handle is an 8px hover affordance, and a 24px
            box at the drawer's edge would sit over the icon column (the collapsed drawer is 54px wide
            with the 24px icons at 18-42px) and over whichever row is at mid-height. */}
        <div className={`hidden max-md:flex max-md:pb-2 ${isExpanded ? "justify-end pe-3" : "justify-center"}`}>
          <ButtonBase
            disableRipple
            onClick={handleSideBarExpandClick}
            aria-label={toggleLabel}
            aria-expanded={isExpanded}
            className="min-w-8 min-h-8 rounded-full outline-none focus-visible:bg-black/10"
          >
            <IconComponent
              name={isExpanded ? "BackwardArrow" : "ForwardArrow"}
              width="1rem"
              height="1rem"
              className="rtl:-scale-x-100"
            />
          </ButtonBase>
        </div>
      </Drawer>
    );
  }

  return (
    <div
      className={`relative flex h-full bg-[#eff5f9] transition-[width] duration-300 ${isExpanded ? "w-62.5" : "w-20"}`}
    >
      {getDrawer()}
      {/* md and up: the edge handle. ButtonBase resets the global `button` rule (RichTextEditor/index.css)
          so the <button> paints exactly like the old <div>; `outline-none` beats that rule's `button:focus`
          ring, and keyboard focus shows the handle at full opacity instead. Below md the toggle lives in
          the drawer (see getDrawer). */}
      <ButtonBase
        disableRipple
        onClick={handleSideBarExpandClick}
        aria-label={toggleLabel}
        aria-expanded={isExpanded}
        className={
          (isExpanded
            ? "h-(--size-35) cursor-pointer border-e-(length:--size-2) border-e-(--text-light) border-solid me-3.25 opacity-30 absolute top-1/2 bottom-1/2 ps-(--size-8) z-10 inset-e-(--size-2) hover:cursor-pointer hover:border-none hover:bg-no-repeat hover:opacity-100 hover:bg-[url('@Assets/icons/svg/BigLeftArrow.svg')]"
            : "pe-(--size-8) h-(--size-35) cursor-pointer border-s-(length:--size-2) border-s-(--text-light) border-solid opacity-30 z-11 top-1/2 absolute bottom-1/2 inset-s-(--size-65) hover:cursor-pointer hover:border-none hover:opacity-100 hover:bg-no-repeat hover:bg-[url('@Assets/icons/svg/BigRightArrow.svg')]") +
          " outline-none focus-visible:opacity-100 max-md:hidden"
        }
      />
    </div>
  );
}

export default Sidebar;
